/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {List, makeStyles} from '@material-ui/core';
import {translate} from '@ion/core/src/locale/locale';
import {AlertSeverity} from '@ion/core/src/types/entities';
import Accordion, {getAccordionWrapperStyles} from '@ion/dls/src/components/accordion/Accordion';
import {IonSpinner} from '@ion/dls/src/components/loading/IonSpinner';
import {useMedia} from '@ion/dls/src/hooks/useMedia';
import {EmptyNotifications} from '@ion/dls/src/notifications/EmptyNotifications';
import {categorizeNotificationByDate} from '@ion/dls/src/notifications/helpers';
import {NotificationCard} from '@ion/dls/src/notifications/NotificationCard';
import {TimeStamp} from '@ion/dls/src/notifications/types';
import {dlsColor, dlsTypography} from '@ion/dls/src/theme/theme';
import {Notification} from '@ion/dls/src/types/extensions';
import {DLSTheme} from '@ion/dls/src/types/types';
import {
  getNotificationDetails,
  isJobNotification,
  markAsCleared,
  markAsRead,
  selectNotification,
  setNotificationPanelState
} from './state';
import ViewEventCta from './ViewEventCta';

export interface NotificationPanelContentProps {
  checkboxesEnabled?: boolean;
  filterUnread?: boolean;
  onReviewClick?: (notification: Notification) => void;
}

interface ListHeightProps {
  count: number;
  isPhone?: boolean;
}

type GroupedNotifications = Partial<Record<TimeStamp, Notification[]>>;

const panelHeight       = 'calc(100vh - 192px)';
const panelHeightMobile = 'calc(100vh - 107px)';
const listHeight        = '83vh';
const mobileAdjustment  = 50;

const listHeightMap = {
  1 : 164,
  2 : 214,
  3 : 264
};

const listHeightCalc = ({count, isPhone}: ListHeightProps): string => {
  const offset = listHeightMap[count] + (isPhone ? mobileAdjustment : 0);

  return offset ? `calc(100vh - ${offset}px)` : listHeight;
};

const accordionOrder = [TimeStamp.TODAY, TimeStamp.YESTERDAY, TimeStamp.OLDER];

const presentGroups = (grouped: GroupedNotifications): TimeStamp[] => (
  accordionOrder.filter(group => grouped?.[group]?.length)
);

const useStyles = makeStyles((theme: DLSTheme) => ({
  notificationPanelList : {
    overflowX     : 'hidden',
    overflowY     : 'auto',
    paddingBottom : '0px'
  },
  loader : {
    height         : 'calc(100vh - 375px)',
    display        : 'flex',
    justifyContent : 'center',
    alignItems     : 'center'
  },
  accordionTitle : {
    ...dlsTypography('panelCards'),
    fontFamily    : 'Roboto',
    fontWeight    : '600',
    fontSize      : '14px',
    lineHeight    : '17px',
    textTransform : 'uppercase',
    color         : dlsColor('black'),
    minHeight     : 48
  },
  accordion : {
    padding : '0 15px'
  },
  ...getAccordionWrapperStyles()
}));

/**
 * @function NotificationPanelContent
 * @returns Node
 *
 * Component that houses the contents of the notification
 * drawer that is opened when clicking the icon in the header
 */
const NotificationPanelContent = ({
  checkboxesEnabled = false,
  filterUnread = false,
  onReviewClick
}: NotificationPanelContentProps) => {
  const classes               = useStyles();
  const dispatch              = useDispatch();
  const {isPhone}             = useMedia();
  const activeTab             = useSelector(({notificationsPanel}) => notificationsPanel.panel.activePanel);
  const detailsState          = useSelector(({notificationsPanel}) => notificationsPanel.panel.state);
  const selectedNotifications = useSelector(({notificationsPanel}) => notificationsPanel.panel.selected);
  const {
    all     : allNotifications,
    urgent  : urgentNotifications,
    loading : isLoading
  }                             = useSelector(({notificationsPanel}) => notificationsPanel);
  const [grouped, setGrouped]   = useState<GroupedNotifications>({});
  const [selected, setSelected] = useState<TimeStamp | null>(TimeStamp.TODAY);

  const groups = presentGroups(grouped);

  // since the urgent tab's value is 0 we can use a truthy/falsy check
  const emptyTitle = activeTab ? translate('notifications:panel.emptyAll') : translate('notifications:panel.emptyUrgent');

  useEffect(() => {
    const source = activeTab ? allNotifications : urgentNotifications;
    const shown  = filterUnread ? source?.filter(notification => !notification?.isRead) : source;

    setGrouped(categorizeNotificationByDate(shown ?? []));
  }, [filterUnread, activeTab, allNotifications, urgentNotifications]);

  useEffect(() => {
    if (groups?.length) {
      setSelected(groups[0]);
    }
  }, [groups.toString()]);

  function readMore(notification: Notification) {
    const {uuid} = notification;
    const temp = [...detailsState];
    let updatedTemp;
    const matchingIndex = detailsState.findIndex(item => item.id === uuid);
    if (matchingIndex > -1) {
      temp[matchingIndex] = {id: uuid, open: true, loading: !notification?.details};
      updatedTemp = temp;
      dispatch(setNotificationPanelState(updatedTemp));
    } else {
      dispatch(setNotificationPanelState(
        [...detailsState, {id: uuid, open: true, loading: !notification?.details}]
      ));
    }
    if (!notification?.details) {
      dispatch(getNotificationDetails(notification));
    }
  }

  function readLess(notification: Notification) {
    const {uuid} = notification;
    const temp = [...detailsState];
    let updatedTemp;
    const matchingIndex = detailsState.findIndex(item => item.id === uuid);
    if (matchingIndex > -1) {
      temp[matchingIndex] = {id: uuid, open: false, loading: false};
      updatedTemp = temp;
      dispatch(setNotificationPanelState(updatedTemp));
    } else {
      dispatch(setNotificationPanelState(
        [...detailsState, {id: uuid, open: false, loading: false}]
      ));
    }
  }

  function markNotificationRead(notification: Notification) {
    if (!notification?.isRead && !isJobNotification(notification)) {
      dispatch(markAsRead(notification));
    }
  }

  function onClearClick(notification) {
    dispatch(markAsCleared(notification));
  }

  function onCardClick(notification: Notification, cardSelected?: boolean) {
    markNotificationRead(notification);
    dispatch(selectNotification(!cardSelected, notification));
  }

  return (
    <>
      {
        !isLoading ? (
          groups.length ? groups.map(function(group) {
            const rows = grouped[group] ?? [];

            return (
              <div className={classes.accordionWrapper} key={group}>
                <Accordion className={classes.accordion}
                  expanded={group === selected}
                  onExpand={function(expand: boolean) {
                    setSelected(expand ? group : null);
                  }}
                  header={
                    <div className={classes.accordionTitle}>
                      {translate(`notifications:panel.accordionTitle.${group.toLowerCase()}`)}
                      ({rows.length})
                    </div>
                  }
                  details={
                    <List className={classes.notificationPanelList}
                      style={{maxHeight: listHeightCalc({count: groups.length, isPhone})}}>
                      {rows.map(function(notification: Notification) {
                        const {
                          isRead,
                          alertConfigurationName,
                          severity = AlertSeverity.MEDIUM,
                          createdOn,
                          uuid,
                          assetName,
                          alertType
                        } = notification;
                        const matchingClick = detailsState?.find(clicked => clicked.id === uuid);
                        const isChecked     = selectedNotifications?.find(active => active.uuid === uuid);

                        return (
                          <NotificationCard key={`notification-card-${uuid}`}
                            type={alertType?.title}
                            text={assetName ?? ''}
                            subText={alertConfigurationName}
                            read={isRead}
                            timeStamp={createdOn}
                            notification={notification}
                            isUrgent={severity === AlertSeverity.CRITICAL}
                            viewEventCta={<ViewEventCta notification={notification} />}
                            onMoreClick={readMore}
                            onLessClick={readLess}
                            moreInfoLoading={matchingClick?.loading}
                            showMoreInfo={matchingClick?.open && !!notification?.details}
                            showCheckBox={checkboxesEnabled}
                            onCardClick={onCardClick}
                            onClearClick={onClearClick}
                            onReviewClick={onReviewClick}
                            isChecked={!!isChecked} />
                        );
                      })}
                    </List>
                  }
                  borderBottom='none'
                  padding='0px 0'
                  isCondensedVariant />
              </div>
            );
          })
            : <EmptyNotifications title={emptyTitle}
              desktopHeight={panelHeight}
              mobileHeight={panelHeightMobile} />

        ): (
          <div className={classes.loader}>
            <IonSpinner />
          </div>
        )
      }
    </>
  );
};

export default NotificationPanelContent;
